import CONSTANTS from "@/constants";
import TimeFormatter from "@/TimeFormatter";
import { shortenPeriodText } from "@/utils/periodLabel";

const state = {
    processedTimeline: [],
    loading: true,
};

const getters = {
    /* src/store/modules/dmr.js */

    getProcessedTimeline: (state) => {
        // JAVÍTÁS (TELJESÍTMÉNY): l. a game.js updateSortedTimeline azonos
        // javítását - a modul-feloldás és az `isStrand` kiszámítása kikerült a
        // komparátorból. Ez egy GETTER, tehát minden újraszámításnál lefut.
        const isStrand = state.gamedata && state.gamedata.rule === 'STRAND';
        const getChronoOrder = require('@/utils/periodOrder').getChronoOrder;

        const sorted = [...state.processedTimeline].sort((a, b) => {
            // 1. LÉPÉS: Periódus szerinti rendezés (Csökkenő sorrend: 3, 2, 1)
            // Ez a legfontosabb! Így a 3-as periódus eseményei mindig a 2-es fölött lesznek,
            // függetlenül attól, hogy a totalGameTime esetleg pár tizeddel "átlóg".
            const periodA = getChronoOrder(a.period || '0', isStrand);
            const periodB = getChronoOrder(b.period || '0', isStrand);

            if (periodA > periodB) return -1; // A (frissebb) előre
            if (periodA < periodB) return 1;  // B (frissebb) előre

            // -----------------------------------------------------
            // Ha a periódus AZONOS, akkor jön a finomhangolás:
            // -----------------------------------------------------

            // 2. LÉPÉS: A 'periodchange' (Félidő kezdete) mindig a blokk ALJÁRA kerüljön.
            // Úgy tekintjük, hogy a kezdés a "legrégebbi" dolog abban a félidőben.

            const isPeriodChangeA = (a.action === 'periodchange');
            const isPeriodChangeB = (b.action === 'periodchange');

            // Ha 'A' a kezdés, küldjük hátra (1), kivéve ha 'B' is kezdés
            if (isPeriodChangeA && !isPeriodChangeB) return 1;
            // Ha 'B' a kezdés, küldjük hátra (-1)
            if (!isPeriodChangeA && isPeriodChangeB) return -1;

            // 3. LÉPÉS: Idő szerinti rendezés (Csökkenő)
            // Ha minden más egyezik, döntsön az idő.
            var aTgt = a.totalGameTime || 0;
            var bTgt = b.totalGameTime || 0;
            if (aTgt > bTgt) return -1;
            if (aTgt < bTgt) return 1;

            // 4. LÉPÉS: Jóváhagyás alapú rendezés
            // A jóváhagyott/elutasított események kerüljenek a pendingek alá.
            const aIsApproved = !!(a.approved || a.rejectReason);
            const bIsApproved = !!(b.approved || b.rejectReason);
            
            if (aIsApproved && !bIsApproved) return 1;
            if (!aIsApproved && bIsApproved) return -1;

            // 5. LÉPÉS: Létrehozási/Szerkesztési idő alapú rendezés (Frissesség szerint)
            // Ha minden más megegyezik (pl. állt az óra és gyorsan felvittek 3 eseményt),
            // a legfrissebb (legkésőbb rögzített) legyen a legfelül!
            const dateA = a.date || a.clientTime || 0;
            const dateB = b.date || b.clientTime || 0;
            
            if (dateA > dateB) return -1;
            if (dateA < dateB) return 1;

            return 0;
        });
        if (process.env.NODE_ENV === 'development') {
            console.log("[DMR SORT] ProcessedTimeline sorted: " + sorted.length + " items");
        }
        return sorted;
    },
    isLoading: state => state.loading
};

const mutations = {
    SET_PROCESSED_TIMELINE(state, list) {
        state.processedTimeline = list;
    },
    ADD_ITEM(state, item) {
        state.processedTimeline.push(item);
    },
    REMOVE_ITEM(state, uuid) {
        state.processedTimeline = state.processedTimeline.filter(i => i.UUID !== uuid);
    },
    UPDATE_ITEM(state, { uuid, updates }) {
        const index = state.processedTimeline.findIndex(i => i.UUID === uuid);
        if (index !== -1) {
            state.processedTimeline.splice(index, 1, {
                ...state.processedTimeline[index],
                ...updates
            });
        }
    },
    SET_LOADING(state, val) {
        state.loading = val;
    }
};

const actions = {
    initDmr({ commit, dispatch, rootState }) {
        const rawTimeline = rootState.game.dmrTimeline || [];

        commit('SET_PROCESSED_TIMELINE', []);
        commit('SET_LOADING', true);

        if (rawTimeline.length) {
            rawTimeline.forEach(item => {
                if (item.action !== 'timeOut' || (item.params && item.params.type !== 'lost')) {
                    dispatch('formatAndAddItem', { item });
                }
            });
        }

        dispatch('reCalculateScores');
        commit('SET_LOADING', false);
    },

    formatAndAddItem({ commit, rootState }, { item }) {
        if (item.deleted) return;

        const getPlayerData = (id) => (rootState.game.dmrPlayers || []).find(p => p.id === id);

        const getPeriod = (periodVal) => {
            if (!rootState.game.gamedata.periods) return {};
            return rootState.game.gamedata.periods.find(p => p.value === periodVal) || {};
        };

        const isPassivePeriod = (periodVal) => {
            const p = getPeriod(periodVal);
            return ['zero', 'extendedzero'].includes(p.type);
        };

        const labels = CONSTANTS.DMR.ACTION_LABELS;
        let newItem = null;

        if (item.action === 'periodchange') {
            const periodItem = getPeriod(item.period);
            newItem = {
                UUID: item.UUID,
                time: TimeFormatter.intToString(periodItem.startat || 0),
                // JAVÍTÁS: a `shortenPeriodText` itt csak a KIÍRT feliratot rövidíti
                // ("O.T." forma) - a `periodItem` maga a megosztott
                // `gamedata.periods` tömb egy eleme (nem másolat), ezért SOSEM
                // szabad rá visszaírni, csak a belőle épített string-en alkalmazni.
                label: (shortenPeriodText(periodItem.text) || '') + (isPassivePeriod(item.period) ? '' : ' kezdete'),
                period: item.period,
                action: item.action,
                totalGameTime: periodItem.startat || 0,
                draft: false
            };
        }
        else if ([...CONSTANTS.DMR.DMR_DRAFTS, ...CONSTANTS.DMR.APPROVE_REQUIRED].includes(item.action) && item.params) {
            let team = (item.params.selectedTeamId === 'teamA' || item.params.selectedTeamId === 'staffA') ? 'A' : 'B';
            let player = getPlayerData(item.params.selectedPlayer);

            if (!player) {
                player = { id: 0, team: team, jersey: '-', name: '' };
            }

            let calcTotalGameTime = item.totalGameTime;
            if ((calcTotalGameTime === undefined || calcTotalGameTime === null) && item.params && item.params.gameTime) {
                const parts = item.params.gameTime.split(':');
                if (parts.length === 2) {
                    const pItem = getPeriod(item.period);
                    const seconds = parseInt(parts[0], 10) * 60 + parseInt(parts[1], 10);
                    calcTotalGameTime = (pItem.startat || 0) + seconds;
                }
            }

            newItem = {
                UUID: item.UUID,
                team: team,
                time: item.params.gameTime,
                label: labels[item.action] || item.action,
                period: item.period,
                player: player,
                action: item.action,
                checked: false,
                approved: Boolean(item.params.approved),
                rejectReason: item.params.rejectReason ? item.params.rejectReason : null,
                totalGameTime: calcTotalGameTime,
                draft: Boolean(item.params.isDraft),
                date: item.date,
                clientTime: item.clientTime
            };
            console.log(`[DMR-FORMAT] ID: ${item.UUID} | action: ${item.action} | player: ${player.name || player.id} | approved: ${newItem.approved} | rejectReason: ${newItem.rejectReason} | clientTime: ${item.clientTime}`);
        }

        if (newItem) {
            // JAVÍTÁS: Ha az elem már létezik a DMR listában, megőrizzük az eredeti
            // date/clientTime értékeket, hogy az elfogadás/elutasítás ne változtassa
            // meg az elem pozícióját a rendezett listában.
            const existingItem = state.processedTimeline.find(i => i.UUID === newItem.UUID);
            if (existingItem) {
                if (existingItem.date) newItem.date = existingItem.date;
                if (existingItem.clientTime && newItem.clientTime !== existingItem.clientTime) {
                    newItem.clientTime = existingItem.clientTime;
                }
            }
            commit('REMOVE_ITEM', newItem.UUID);
            commit('ADD_ITEM', newItem);
        }
    },

    reCalculateScores({ rootState, commit }) {
        // FŐ TIMELINE HASZNÁLATA
        const mainTimeline = rootState.game.timelineData || [];

        let teamAScores = 0;
        let teamBScores = 0;

        mainTimeline.forEach(obj => {
            const isDeleted = (obj.deleted == 1 || obj.deleted === true) ||
                (obj.params && (obj.params.deleted == 1 || obj.params.deleted === true));

            if (obj.params && obj.params.selectedAction && !isDeleted) {
                const actionId = obj.params.selectedAction.actionId;

                if (CONSTANTS.ACTIONS.SINGLE_SCORE_ACTIONS.includes(actionId) ||
                    CONSTANTS.ACTIONS.DOUBLE_SCORE_ACTIONS.includes(actionId)) {

                    let increase = CONSTANTS.ACTIONS.DOUBLE_SCORE_ACTIONS.includes(actionId) ? 2 : 1;

                    if (obj.params.selectedTeamId === 'teamA') {
                        teamAScores += increase;
                    } else if (obj.params.selectedTeamId === 'teamB') {
                        teamBScores += increase;
                    }
                }
            }
        });

        commit('game/UPDATE_GAMEDATA_SCORES', { teamA: teamAScores, teamB: teamBScores }, { root: true });
    },

    handleDmrPush({ dispatch, commit }, data) {
        if (data.type === 'periodchange' || (data.action === 'timeOut' && data.params.type === 'lost')) {
            return;
        }

        commit('game/UPDATE_TIMELINE_EVENT', data, { root: true });

        dispatch('formatAndAddItem', { item: data });
        dispatch('reCalculateScores');
    }
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions
};
